import { Component, EventEmitter, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Volunteer } from '../../../core/models/volunteer.model';
import { CelebrationJobLocation } from '../../../core/models/event.model';
import { VolunteerService } from '../../../core/services/volunteer.service';
import { CheckoutService } from '../../../core/services/checkout.service';
import { MeuLocalService } from '../../../core/services/meu-local.service';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';

@Component({
  selector: 'app-volunteer-step',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatCardModule, MatInputModule, MatButtonModule, MatIconModule, MatProgressSpinnerModule, MatSnackBarModule],
  templateUrl: './volunteer-step.component.html',
  styleUrls: ['./volunteer-step.component.scss'],
  providers: [VolunteerService]
})
export class VolunteerStepComponent {
  @Output() volunteerSelected = new EventEmitter<Volunteer>();
  /** Emite quando o operador escolhe iniciar já no local onde o voluntário está agendado. */
  @Output() iniciarLocalDefinido = new EventEmitter<{ volunteer: Volunteer; jobLocation: CelebrationJobLocation }>();

  volunteer: Volunteer | null = null;
  error: string | null = null;
  success: string | null = null;
  isLoading: boolean = false;
  cpfForm: FormGroup;

  constructor(
    private fb: FormBuilder,
    private volunteerService: VolunteerService,
   private checkoutService: CheckoutService,
   private meuLocalService: MeuLocalService,
   private snackBar: MatSnackBar,) {
    this.cpfForm = this.fb.group({
      cpf: ['', Validators.required]
    });
  }

  onSearch() {
    if (this.cpfForm.valid) {
      this.isLoading = true;
      this.error = null;
      this.volunteer = null;
      this.volunteerService.getVolunteerByCpf(this.cpfForm.value.cpf).subscribe({

        next: (data: Volunteer & { erro?: string }) => {
          if (data.erro) {
            this.error = data.erro;
            this.isLoading = false;
            this.volunteer = null;
            return;
          }
          this.volunteer = data;
          console.log(this.volunteer)
          this.isLoading = false;
        },
        error: () => {
          this.error = 'Erro ao buscar voluntário. Por favor, verifique o CPF e tente novamente.';
          this.isLoading = false;
        }
      });
    }

  }

  onSelect() {
    if (this.volunteer) {
      this.volunteerSelected.emit(this.volunteer);
    }
  }

  /** True quando o voluntário já está agendado (aguardando início) em um local. */
  get isAgendado(): boolean {
    const ev = this.volunteer?.staff?.currentEvent?.eventType;
    if (!ev) return false;
    const enumVal = (ev.eventTypeEnum ?? '').toString().trim().toLowerCase();
    const nome = (ev.name ?? '').toString().trim().toLowerCase();
    // Tolerante à variação de enum entre endpoints; usa o nome exibido como reforço.
    return enumVal === 'scheduled' || enumVal === 'agendado' || nome === 'agendado';
  }

  /**
   * Botão "Iniciar já no lugar definido": pula a seleção de local e segue
   * para o agendamento com o local onde o voluntário já está agendado.
   */
  iniciarNoLocalDefinido() {
    const staff = this.volunteer?.staff;
    if (!this.volunteer || !staff) return;

    const local = staff.location;
    const jobLocation: CelebrationJobLocation = {
      celebrationJobLocationId: staff.celebrationJobLocationUuid,
      // No fluxo do wizard, locationUuid é o id usado como celebrationJobLocationId no createStaff.
      locationUuid: staff.celebrationJobLocationUuid ?? local?.locationUuid ?? '',
      name: local?.name ?? '',
      description: local?.description ?? '',
      geoLocation: local?.geoLocation ?? null,
      activeStaff: local?.activeStaff ?? 0,
      scheduledStaff: local?.scheduledStaff ?? 0,
      maxStaff: local?.maxStaff ?? 0,
      minStaff: local?.minStaff ?? 0,
      priority: local?.priority ?? 0,
      defaultJobTimeInMinutes: local?.defaultJobTimeInMinutes ?? 0,
    };

    this.iniciarLocalDefinido.emit({ volunteer: this.volunteer, jobLocation });
  }

  /**
   * Botão "Iniciar no meu local": usa o local salvo pelo usuário (menu "Meu Local")
   * e segue para o agendamento, pulando a seleção — igual ao "local definido".
   */
  iniciarNoLocalUsuarioLogado() {
    if (!this.volunteer) return;

    const meuLocal = this.meuLocalService.meuLocal;
    if (!meuLocal) {
      this.snackBar.open('Você ainda não definiu seu local. Configure em "Meu Local".', 'OK', { duration: 4000 });
      return;
    }

    this.iniciarLocalDefinido.emit({ volunteer: this.volunteer, jobLocation: meuLocal });
  }

  getWhatsappLink(telefone: string | undefined | null): string {
    if (!telefone) return '';
    const numero = telefone.replace(/\D/g, '');
    // Considera válido apenas se tiver pelo menos 10 dígitos
    if (numero.length < 10) return '';
    return `https://wa.me/${numero}`;
  }
   performCheckout() {
    if (!this.volunteer?.id) return;
    this.isLoading = true;
    this.error = null;
    const payload = {
      memberId: this.volunteer.id,
      description: "",
    };
    this.checkoutService.checkoutStaff(payload).subscribe({
      next: (response) => {
        this.isLoading = false;
        this.error = null;
        this.success = 'Checkout realizado com sucesso!';
        this.resetForm();
      },
      error: (error: any) => {
        this.error = error.error?.message || 'Erro ao realizar checkout.';
        this.isLoading = false;
        this.resetForm();
      }
    });
  }

  resetForm() {
    if (this.volunteer) {
      this.volunteer.staff = undefined;
    }
    setTimeout(() => {
      this.error = null;
      this.success = null;
    }, 3500);
  }
}
